"use strict";Object.defineProperty(exports, "__esModule", {value: true}); function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; } function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }var _react = require('react');


var _actions = require('shared/actions'); var _actions2 = _interopRequireDefault(_actions);
var _broadcast = require('shared/actions/broadcast');
var _useBus = require('shared/hooks/useBus'); var _useBus2 = _interopRequireDefault(_useBus);
var _store = require('shared/store'); var _store2 = _interopRequireDefault(_store);
var _broadcast3 = require('shared/selectors/broadcast');
var _app = require('shared/selectors/app');
var _hooks = require('web/services/hooks');

var _campaignState = require('./campaignState');

/* Rev 27 P2: each campaign's start time, shared by everything that shows it
 * (header, sidebar, controls, progress bar), so one small request serves
 * them all. An entry belongs to the queued count it was read for. While a
 * read is out, `isLoading`; a failed read keeps `startAt` unknown and is
 * tried again after a pause or when the window comes back (review R800-38).
 * A known time is read again (once, for all views) after an authoritative
 * refresh of the campaign, when the window comes back, and when a view
 * returns after all had gone, since another client may have rescheduled the
 * same count (Rev 27 FU1). */











// Per store, so a new session (or test) starts empty.
const caches = new WeakMap()
const NO_STORE = {}
const cache = () => {
  const store = _nullishCoalesce(_store2.default.call(void 0, ), () => ( NO_STORE))
  let entries = caches.get(store)
  if (!entries) caches.set(store, entries = new Map())
  return entries
}
/* Rev 28 R3: a schedule this client just sent, shown from the chosen time
 * until the server answers, so the page goes straight to Scheduled. It's a
 * status only: counts and rows stay the server's (review R800-45). */



const seedsByStore = new WeakMap()
const seeds = () => {
  const store = _nullishCoalesce(_store2.default.call(void 0, ), () => ( NO_STORE))
  let entries = seedsByStore.get(store)
  if (!entries) seedsByStore.set(store, entries = new Map())
  return entries
}
const listeners = new Set()
const emit = () => listeners.forEach(l => l())
const subscribe = (l) => {
  listeners.add(l)
  return () => { listeners.delete(l) }
}
let lastId = 0
// Rev 27 FU2: each campaign's invalidations (an authoritative refresh, its
// last view going), counted even while a read is out. A reply whose read
// began before the latest one is kept only as stale, so the views read
// again once it settles.
const generations = new Map()
const generation = (campaignKey) => _nullishCoalesce(generations.get(campaignKey), () => ( 0))
const invalidate = (campaignKey) => {
  generations.set(campaignKey, generation(campaignKey) + 1)
}
const put = (campaignKey, entry) => {
  cache().set(campaignKey, entry)
}

// After a failed read: 5 s, 10 s, 20 s, then every 30 s while it's shown.
const retryDelay = (failures) => Math.min(5000 * 2 ** (failures - 1), 30000)
const retries = new Map()
const cancelRetry = (campaignKey) => {
  clearTimeout(retries.get(campaignKey))
  retries.delete(campaignKey)
}

const load = (campaignKey, queued) => {
  const entries = cache()
  const was = entries.get(campaignKey)
  const id = ++lastId
  const startedIn = generation(campaignKey)
  const sameCount = _optionalChain([was, 'optionalAccess', _ => _.queued]) === queued
  cancelRetry(campaignKey)
  put(campaignKey, {
    queued,
    id,
    isLoading: true,
    startAt: sameCount ? was.startAt : undefined,
    failures: sameCount ? was.failures : 0,
  })
  emit()
  const isCurrent = () => _optionalChain([entries, 'access', _2 => _2.get, 'call', _3 => _3(campaignKey), 'optionalAccess', _4 => _4.id]) === id
  _actions2.default.findCampaignStartTime(campaignKey)
    .then(startAt => {
      if (!isCurrent()) return
      // Landing after every view has gone, or after an invalidation since it
      // was asked, it's already stale.
      const isStale = !mounted.get(campaignKey) || generation(campaignKey) !== startedIn
      put(campaignKey, { queued, id, isLoading: false, startAt, failures: 0, isStale })
      emit()
    })
    .catch((e) => {
      log.error(e )
      const entry = entries.get(campaignKey)
      if (!entry || entry.id !== id) return
      const failures = entry.failures + 1
      put(campaignKey, { ...entry, isLoading: false, failures })
      retries.set(campaignKey, setTimeout(() => {
        retries.delete(campaignKey)
        if (isCurrent() && mounted.get(campaignKey)) load(campaignKey, queued)
      }, retryDelay(failures)))
      emit()
    })
}

/* Reads the time unless a read for this count is out, done, or failed and
 * waiting to be tried again. */
const ensure = (campaignKey, queued) => {
  const entry = cache().get(campaignKey)
  const isSettled = _optionalChain([entry, 'optionalAccess', _5 => _5.isLoading])
    || (!_optionalChain([entry, 'optionalAccess', _6 => _6.isStale]) && (_optionalChain([entry, 'optionalAccess', _7 => _7.startAt]) !== undefined || retries.has(campaignKey)))
  if (_optionalChain([entry, 'optionalAccess', _8 => _8.queued]) === queued && isSettled) return
  load(campaignKey, queued)
}

/* Marks a known time to be read again; the views' effect then reads it once
 * (ensure), keeping the last time shown meanwhile. An authoritative refresh
 * also invalidates a read already out (it may predate the change), which
 * then comes back stale and is followed by a fresh one. A hint (the window
 * coming back, a reconnect) leaves a read already out to answer: it was
 * sent after the hint. */
const markStale = (campaignKey, isAuthoritative = false) => {
  if (isAuthoritative) invalidate(campaignKey)
  const entry = cache().get(campaignKey)
  if (!entry || entry.isLoading || entry.isStale) return
  put(campaignKey, { ...entry, isStale: true })
  emit()
}

/* After this client starts or cancels a send, read the time again even if
 * the queued count comes back the same. */
 const forgetStartTime = (campaignKey) => {
  cancelRetry(campaignKey)
  if (cache().delete(campaignKey)) emit()
}; exports.forgetStartTime = forgetStartTime

/* Rev 28 R3: on pressing Schedule, before the start is sent: every view
 * reads Scheduled from the chosen time while the server answers. */
 const seedStartTime = (campaignKey, startAt) => {
  seeds().set(campaignKey, { startAt })
  emit()
}; exports.seedStartTime = seedStartTime

/* The start was refused: back to what the campaign was. */
 const dropStartTime = (campaignKey) => {
  if (seeds().delete(campaignKey)) emit()
}; exports.dropStartTime = dropStartTime

/* The start succeeded: the chosen time becomes the time for the queued count
 * the server returned, marked stale so one read confirms it in the
 * background (another client's time replaces it silently). */
 const confirmStartTime = (campaignKey) => {
  const seed = seeds().get(campaignKey)
  seeds().delete(campaignKey)
  cancelRetry(campaignKey)
  invalidate(campaignKey)
  const c = _broadcast3.selectCampaignByKey.call(void 0, _store2.default.call(void 0, ).getState(), campaignKey)
  const queued = c && c.Stats.processing === 0 ? c.Stats.queued : 0
  if (seed && queued) {
    put(campaignKey, { queued, id: ++lastId, isLoading: false, startAt: seed.startAt, failures: 0, isStale: true })
  } else {
    cache().delete(campaignKey)
  }
  emit()
}; exports.confirmStartTime = confirmStartTime

const REFRESH_ACTIONS = [
  _broadcast.actionCreators.getCampaignActions.SUCCESS.type,
  _broadcast.actionCreators.findCampaignsActions.SUCCESS.type,
]

// How many mounted hooks show each campaign. Once none do (e.g. on leaving
// Broadcast), the time goes stale: another client may reschedule the same
// count meanwhile, so the next view reads it again.
const mounted = new Map()

/* Re-renders at `at` (if ahead), and whenever the window comes back, since a
 * sleeping tab's timers fire late. Each wake-up re-arms until `at` is due:
 * setTimeout takes at most ~24.8 days, and a clock set back wakes it early
 * (review R800-39). */
const useRecheckAt = (at) => {
  const [tick, recheck] = _react.useReducer.call(void 0, (n) => n + 1, 0)
  _react.useEffect.call(void 0, 
    () => {
      const ms = at ? Date.parse(at) - Date.now() : NaN
      if (!(ms > 0)) return
      const timer = setTimeout(recheck, Math.min(ms, 2 ** 31 - 1))
      const onVisible = () => { if (document.visibilityState === 'visible') recheck() }
      window.addEventListener('focus', recheck)
      document.addEventListener('visibilitychange', onVisible)
      return () => {
        clearTimeout(timer)
        window.removeEventListener('focus', recheck)
        document.removeEventListener('visibilitychange', onVisible)
      }
    },
    [at, tick],
  )
}

/* Whether `at` is still ahead, re-checked at that time (Q6: a row reads
 * Scheduled until its time). */
 const useIsAhead = (at) => {
  useRecheckAt(at)
  return !!at && Date.parse(at) > Date.now()
}; exports.useIsAhead = useIsAhead











/* Whether the campaign is Scheduled (or Paused before it starts), and when it
 * starts. Only a campaign with queued messages and none processing could be
 * scheduled, so only those are looked up. */
const useCampaignSchedule = (campaignKey) => {
  const campaign = _hooks.useAppSelector.call(void 0, state => _broadcast3.selectCampaignByKey.call(void 0, state, campaignKey))
  const isOnline = _hooks.useAppSelector.call(void 0, _app.selectIsOnline)
  const queued = campaign && campaign.Stats.processing === 0 ? campaign.Stats.queued : 0
  const entry = _react.useSyncExternalStore.call(void 0, subscribe, () => cache().get(campaignKey))
  // The seed lasts until the start's reply or refusal, whatever Active
  // says meanwhile: the server reports progress before it activates
  // (review R800-45).
  const pending = _react.useSyncExternalStore.call(void 0, subscribe, () => seeds().get(campaignKey))

  _react.useEffect.call(void 0, 
    () => {
      mounted.set(campaignKey, (_nullishCoalesce(mounted.get(campaignKey), () => ( 0))) + 1)
      return () => {
        const left = (_nullishCoalesce(mounted.get(campaignKey), () => ( 1))) - 1
        if (left) {
          mounted.set(campaignKey, left)
          return
        }
        mounted.delete(campaignKey)
        cancelRetry(campaignKey)
        invalidate(campaignKey)
        const e = cache().get(campaignKey)
        if (e) put(campaignKey, { ...e, isStale: true })
      }
    },
    [campaignKey],
  )

  _react.useEffect.call(void 0, 
    () => {
      // A schedule being sent is read once the server confirms it.
      if (pending) return
      if (!queued) exports.forgetStartTime.call(void 0, campaignKey)
      else ensure(campaignKey, queued)
    },
    [campaignKey, queued, entry, pending],
  )

  // A reconnect may have missed a change, and a failed read gets another go
  // when the window comes back.
  const wasOnline = _react.useRef.call(void 0, isOnline)
  _react.useEffect.call(void 0, 
    () => {
      const reconnected = isOnline && !wasOnline.current
      wasOnline.current = isOnline
      if (reconnected) markStale(campaignKey)
    },
    [isOnline, campaignKey],
  )
  // An authoritative read of this campaign (or the list) may carry a same-
  // count reschedule from another client.
  const onRefresh = _react.useCallback.call(void 0, 
    (action) => {
      const key = _optionalChain([(action ), 'access', _9 => _9.payload, 'optionalAccess', _10 => _10.CampaignKey])
      if (!key || key === campaignKey) markStale(campaignKey, true)
    },
    [campaignKey],
  )
  _useBus2.default.call(void 0, REFRESH_ACTIONS, onRefresh)
  _react.useEffect.call(void 0, 
    () => {
      if (!queued) return
      // A failed read gets another go at once; a known one is read again.
      const recheck = () => {
        const e = cache().get(campaignKey)
        if (e && !e.isLoading && e.startAt === undefined) load(campaignKey, queued)
        else markStale(campaignKey)
      }
      const onVisible = () => { if (document.visibilityState === 'visible') recheck() }
      window.addEventListener('focus', recheck)
      document.addEventListener('visibilitychange', onVisible)
      return () => {
        window.removeEventListener('focus', recheck)
        document.removeEventListener('visibilitychange', onVisible)
      }
    },
    [campaignKey, queued],
  )

  const known = queued && _optionalChain([entry, 'optionalAccess', _11 => _11.queued]) === queued ? entry.startAt : null
  const startAt = _nullishCoalesce(_nullishCoalesce(_optionalChain([pending, 'optionalAccess', _12 => _12.startAt]), () => ( known)), () => ( null))
  useRecheckAt(startAt)
  if (pending) {
    const isAhead = Date.parse(pending.startAt) > Date.now()
    return {
      state: isAhead ? 'scheduled' : null,
      startAt: isAhead ? pending.startAt : null,
      isKnown: true,
      isPending: true,
    }
  }
  // Progress notifications re-render with a new campaign, which checks the
  // clock again here too.
  const state = campaign ? _campaignState.scheduleState.call(void 0, campaign, startAt, Date.now()) : null
  return { state, startAt: state ? startAt : null, isKnown: known !== undefined, isPending: false }
}

exports. default = useCampaignSchedule
